import type {ComponentProps} from 'react';
import gql from 'graphql-tag';
import {EzNavigation} from '@ezcater/recipe';
import {NavLink, useHistory} from 'react-router-dom';
import {useCurrentAccount} from 'components/CurrentAccount';
import {useMutation} from 'services/apollo';
import {useFlashMessages} from '@ezcater/flash-messages';
import {
  type HeaderSessionDestroyMutation,
  type HeaderSessionDestroyMutationVariables,
} from 'services/graphql/types';
import {REQUEST_SIGN_IN_LINK_PATH} from 'paths';

export const SessionDestroyMutation = gql(/* GraphQL */ `
  mutation HeaderSessionDestroyMutation($input: DriverAccountSessionDestroyInput! = {}) {
    sessionDestroy: driverAccountSessionDestroy(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

interface HeaderProps {
  children: React.ReactNode;
  orderNumber?: string;
}

const Header = ({children, orderNumber}: HeaderProps) => {
  const history = useHistory();

  const handleError = () => {
    addFlashMessage({
      type: 'error',
      message: 'There was an error logging you out.  Please try again.',
    });
  };

  const [signOut] = useMutation<
    HeaderSessionDestroyMutation,
    HeaderSessionDestroyMutationVariables
  >(SessionDestroyMutation, {
    onError: handleError,
  });
  const {addFlashMessage} = useFlashMessages();
  const helpLink = orderNumber ? `/help/${orderNumber.replace(/[^0-9a-zA-Z]/g, '')}` : '/help';
  const {id} = useCurrentAccount();
  const signOutClick = async () => {
    const response = await signOut();

    if (response?.data?.sessionDestroy) {
      if (response.data.sessionDestroy.userErrors.length) {
        handleError();
      } else {
        history.push(REQUEST_SIGN_IN_LINK_PATH);
      }
    }
  };

  const helpElement = {to: helpLink, label: 'Help', as: NavLink};
  const getNavLinks = (): ComponentProps<typeof EzNavigation>['links'] => {
    if (id) {
      return [
        {to: '/deliveries', label: 'My Deliveries', as: NavLink},
        {to: helpLink, label: 'Help', as: NavLink},
        {href: '#', label: 'Sign Out', onClick: signOutClick},
      ];
    }

    return [helpElement, {to: REQUEST_SIGN_IN_LINK_PATH, label: 'Sign In', as: NavLink}];
  };

  return (
    <EzNavigation
      home={{
        to: '#', // TODO: Change to deliveries page
        label: 'ezCater',
        logo: {src: '/static/images/brand/white-logo.svg', width: 100},
        as: NavLink,
      }}
      links={getNavLinks()}
    >
      {children}
    </EzNavigation>
  );
};

export default Header;
